import React from 'react'
import _cx from 'classnames/bind'
import { noop } from 'lodash'
import { Divider, Icon, PopupTooltip, utils as coreUtils } from '@adc/ui-core'
import cls from './styles.scss'
import { OnlineAuctionSegmentBid } from '@adc/sdk/types'
import { CostView } from '../../bidConfirmation/costs'
import TraditionalOfferActionFooter from '../traditionalOfferActionFooter'

const cx = _cx.bind(cls)

const BID_PENDING = 'Accepting the offer now...'

const BID_AMOUNT_CHANGED =
  'The bid changed while you were accepting, do you want to accept at the new price?'

const ACKNOWLEDGE_TEXT =
  'I acknowledge that I have reviewed the buyer’s set offer, terms, contingencies, and any other additional information relevant to the sale of my property prior to accepting this offer.'

interface AcceptBidContentProps {
  isAcceptingBid: boolean
  bid: OnlineAuctionSegmentBid
  disclaimerAcknowledged: boolean
  setDisclaimerAcknowledged: (checked: boolean) => void
}

const Tooltip = ({ content }: { content: React.ReactNode }) => (
  // @ts-ignore next
  <PopupTooltip
    content={content}
    wide={false}
    trigger={
      <span className='u-ml-1'>
        <Icon
          size='small'
          color='gray'
          name='info-circle'
          fitted
          iconStyle='solid'
        />
      </span>
    }
  />
)

export const AcceptBidContent = ({
  isAcceptingBid,
  bid,
  disclaimerAcknowledged = false,
  setDisclaimerAcknowledged = noop,
}: AcceptBidContentProps) => {
  const terms = bid.terms
  const { sellerCommissionsAmount, sellerConcessionAmount, afterAdjustments } =
    coreUtils.traditionalOffer.getOfferTermsCalculations(bid)

  const marketPlaceFeeAmount = terms?.market_place_fee_amount
  const sellerNetOfferAmount = terms?.seller_net_offer_amount
  return (
    <div>
      <div className={cx('bid-pill-container')}>
        <div className={cx('bid-pill')} data-elm-id='amount_to_bid'>
          <span>
            Net Offer Amount
            <Tooltip content='The value of the offer after deducting any concessions requested by buyer and commissions to be paid by seller. The net offer amount is not the net proceeds that will be received by the seller at closing.' />
          </span>
          {/* @ts-ignore next */}
          <span>{coreUtils.formatters.formatMoney(sellerNetOfferAmount)}</span>
        </div>
      </div>
      <div
        data-elm-id='accept_bid_calculations'
        className={cx('u-mb-3', 'u-mr-3', 'u-ml-3')}
      >
        <CostView
          elmId='cost_calculation_offer_bid'
          label='Purchase Price'
          value={terms?.offer_amount || 0}
        />
        <CostView
          elmId='cost_calculation_fee_bid'
          label='Marketplace Fee'
          value={marketPlaceFeeAmount || 0}
          operator='-'
          valueExplanation={`${
            (terms?.market_place_fee_percentage || 0) * 100
          }% of Purchase Price`}
          tooltip={
            <Tooltip content='Auction.com charges a marketplace fee to cover listing agent commissions, marketing and technology costs from Auction.com.' />
          }
        />
        {sellerCommissionsAmount ? (
          <CostView
            elmId='cost_calculation_commission_bid'
            label={`Seller-paid Buyer's Agent Commission`}
            value={sellerCommissionsAmount}
            operator='-'
            tooltip={
              <Tooltip content="The amount the buyer is asking the seller to pay to the buyer's agent as a commission." />
            }
          />
        ) : null}
        {sellerConcessionAmount ? (
          <CostView
            elmId='cost_calculation_seller_plaid_cost_bid'
            label='Seller Paid Closing Costs'
            value={sellerConcessionAmount}
            operator='-'
            tooltip={
              <Tooltip content="The amount the buyer is requesting the seller to contribute towards the buyer's closing costs." />
            }
          />
        ) : null}
      </div>
      <Divider />
      <div
        data-elm-id='accept_bid_calculations_result'
        className={cx('u-mb-3', 'u-mr-3', 'u-ml-3', 'u-mt-3')}
      >
        <CostView
          elmId='cost_calculation_after_adjustments'
          label='Net Offer Amount'
          value={sellerNetOfferAmount || 0}
          labelWeight='heavy'
          valueWeight='heavy'
          valueBackground='grayExtraLight'
          tooltip={
            <Tooltip content='The value of the offer after deducting any concessions requested by buyer and commissions to be paid by seller. The net offer amount is not the net proceeds that will be received by the seller at closing.' />
          }
        />
      </div>
      <TraditionalOfferActionFooter
        disclaimerAcknowledged={disclaimerAcknowledged}
        setDisclaimerAcknowledged={setDisclaimerAcknowledged}
        isLoading={isAcceptingBid}
        bidAmount={afterAdjustments}
        loadingText={BID_PENDING}
        acknowledgeText={ACKNOWLEDGE_TEXT}
        alertText={BID_AMOUNT_CHANGED}
      />
    </div>
  )
}

export default AcceptBidContent
